import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

import {
  ADMIN_QUERY_KEYS,
  type AdminUsersResponse,
  type UserAssignmentsResponse,
  type UserSubordinatesResponse,
} from "./adminTypes"

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export function useAdminUsers() {
  return useQuery<AdminUsersResponse>({
    queryKey: ADMIN_QUERY_KEYS.users,
    queryFn: () => apiFetch<AdminUsersResponse>("/api/admin/users"),
    retry: false,
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      username: string
      email: string
      password: string
      role: string
    }) => {
      const fd = new FormData()
      fd.append("username", data.username)
      fd.append("email", data.email)
      fd.append("password", data.password)
      fd.append("role", data.role)
      return apiFetch<{ success: boolean; message: string }>("/api/admin/users", {
        method: "POST",
        body: fd,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users })
    },
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${userId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users })
    },
  })
}

export function useChangeUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { userId: number; role: string }) => {
      const fd = new FormData()
      fd.append("role", data.role)
      return apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.userId}/role`,
        { method: "PUT", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users })
    },
  })
}

export function useUpdateUserNetworks() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { userId: number; networkSlugs: string[] }) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.userId}/networks`,
        {
          method: "PUT",
          body: { network_slugs: data.networkSlugs },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.users })
      qc.invalidateQueries({ queryKey: ["auth", "me"] })
    },
  })
}

// ---------------------------------------------------------------------------
// User assignments
// ---------------------------------------------------------------------------

export function useUserAssignments(userId: number | null) {
  return useQuery<UserAssignmentsResponse>({
    queryKey: ADMIN_QUERY_KEYS.userAssignments(userId ?? 0),
    queryFn: () =>
      apiFetch<UserAssignmentsResponse>(
        `/api/admin/users/${userId}/assignments`,
      ),
    enabled: userId !== null,
    retry: false,
  })
}

export function useAssignAccountToUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { userId: number; accountId: number }) => {
      const fd = new FormData()
      fd.append("account_id", String(data.accountId))
      return apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.userId}/assign-account`,
        { method: "POST", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

export function useUnassignAccountFromUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { userId: number; accountId: number }) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.userId}/unassign-account/${data.accountId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

// ---------------------------------------------------------------------------
// Subordinates
// ---------------------------------------------------------------------------

export function useUserSubordinates(userId: number | null) {
  return useQuery<UserSubordinatesResponse>({
    queryKey: ADMIN_QUERY_KEYS.userSubordinates(userId ?? 0),
    queryFn: () =>
      apiFetch<UserSubordinatesResponse>(
        `/api/admin/users/${userId}/subordinates`,
      ),
    enabled: userId !== null,
    retry: false,
  })
}

export function useAssignSubordinate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { teamLeaderId: number; subordinateId: number }) => {
      const fd = new FormData()
      fd.append("subordinate_id", String(data.subordinateId))
      return apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.teamLeaderId}/assign-subordinate`,
        { method: "POST", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

export function useUnassignSubordinate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { teamLeaderId: number; subordinateId: number }) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/admin/users/${data.teamLeaderId}/unassign-subordinate/${data.subordinateId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}
